/**
 * Shared signature capture mechanics.
 *
 * Both signature pads (the inline one in the form-fill flow and the modal one
 * on PDF fills) draw the same way and turn a typed name into the same PNG.
 * Only the commit channel and the surrounding chrome differ, so those stay in
 * the hooks and everything below is shared.
 */

/**
 * Wires pointer drawing onto a canvas and returns a small handle for the
 * operations both hooks need. Sizes the backing store for the device pixel
 * ratio, so all coordinates below are CSS pixels.
 */
export function attachSignatureCanvas(canvas, { onStrokeEnd } = {}) {
  const ctx = canvas.getContext("2d");
  const dpr = window.devicePixelRatio || 1;
  const rect = canvas.getBoundingClientRect();
  const width = rect.width;
  const height = rect.height || 100;

  canvas.width = width * dpr;
  canvas.height = height * dpr;
  ctx.scale(dpr, dpr);
  ctx.strokeStyle = "#111827";
  ctx.lineWidth = 2;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  let isDrawing = false;

  const getPoint = (e) => {
    const bounds = canvas.getBoundingClientRect();
    return {
      x: (e.touches ? e.touches[0].clientX : e.clientX) - bounds.left,
      y: (e.touches ? e.touches[0].clientY : e.clientY) - bounds.top,
    };
  };

  const startDraw = (e) => {
    isDrawing = true;
    const pt = getPoint(e);
    ctx.beginPath();
    ctx.moveTo(pt.x, pt.y);
    e.preventDefault();
  };

  const continueDraw = (e) => {
    if (!isDrawing) return;
    const pt = getPoint(e);
    ctx.lineTo(pt.x, pt.y);
    ctx.stroke();
    e.preventDefault();
  };

  const endDraw = () => {
    if (!isDrawing) return;
    isDrawing = false;
    if (onStrokeEnd) onStrokeEnd(canvas.toDataURL("image/png"));
  };

  canvas.addEventListener("pointerdown", startDraw);
  canvas.addEventListener("pointermove", continueDraw);
  canvas.addEventListener("pointerup", endDraw);
  canvas.addEventListener("pointerleave", endDraw);
  canvas.addEventListener("pointercancel", endDraw);

  return {
    width,
    height,
    clear() {
      ctx.clearRect(0, 0, width, height);
    },
    restore(dataUrl) {
      if (!dataUrl || !dataUrl.startsWith("data:")) return;
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, width, height);
      img.src = dataUrl;
    },
    toDataURL() {
      return canvas.toDataURL("image/png");
    },
  };
}

/**
 * Renders a typed name as a cursive PNG.
 *
 * The background stays transparent: the PNG is stamped onto the PDF, where an
 * opaque white box would cover underlying document text.
 */
export function typedSignatureDataUrl(
  name,
  { width = 400, height = 100, fontSize = 44, minFontSize = 12, padding = 16 } = {},
) {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");

  let size = fontSize;
  ctx.font = `${size}px cursive`;
  // Shrink the font for long names so nothing gets clipped off the canvas
  const maxWidth = width - padding * 2;
  const textWidth = ctx.measureText(name).width;
  if (textWidth > maxWidth) {
    size = Math.max(minFontSize, Math.floor((size * maxWidth) / textWidth));
    ctx.font = `${size}px cursive`;
  }

  ctx.fillStyle = "#111827";
  ctx.textBaseline = "middle";
  ctx.fillText(name, padding, height / 2);
  return canvas.toDataURL("image/png");
}
